import {
  BgColorsOutlined,
  EyeOutlined,
  FontSizeOutlined,
} from "@ant-design/icons";
import {
  theme as antTheme,
  Button,
  Card,
  ColorPicker,
  Divider,
  Flex,
  Select,
  Typography,
} from "antd";
import type { Color } from "antd/es/color-picker";
import { useCallback, useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { ErrorScreen } from "../components/ErrorScreen";
import { useAuthContext } from "../contexts/auth/useAuthContext";
import { useToast } from "../contexts/toast/useToast";
import { useUpdateThemeMutation } from "../graphql/mutation.updateTheme";
import { Theme, useThemeQuery } from "../graphql/query.theme";
import { getAllFontSizes, getAvailableFonts } from "../helpers/fonts";
import { useDebounce } from "../hooks/useDebounce";
import GlobalLoader from "./GlobalLoader";

const { Paragraph, Text, Title } = Typography;

const BrandingPage = () => {
  const { token } = antTheme.useToken();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { authToken, hasSubscription } = useAuthContext();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isIframeLoading, setIsIframeLoading] = useState(true);
  const [barColors, setBarColors] = useState<string[]>([]);
  const [textColor, setTextColor] = useState<string>("");
  const [backgroundColor, setBackgroundColor] = useState<string>("");
  const [theme, setTheme] = useState<Theme | null>(null);
  const [availableFonts, setAvailableFonts] = useState<string[]>([]);
  const [listFont, setListFont] = useState<string>("");
  const [listFontSize, setListFontSize] = useState<number | null>(null);
  const [timelineFont, setTimelineFont] = useState<string>("");
  const [timelineFontSize, setTimelineFontSize] = useState<number | null>(null);
  const { open } = useToast();
  const debouncedBarColors = useDebounce(barColors, 500);
  const debouncedTextColor = useDebounce(textColor, 500);
  const debouncedBackgroundColor = useDebounce(backgroundColor, 500);
  const debouncedListFont = useDebounce(listFont, 500);
  const debouncedListFontSize = useDebounce(listFontSize, 500);
  const debouncedTimelineFont = useDebounce(timelineFont, 500);
  const debouncedTimelineFontSize = useDebounce(timelineFontSize, 500);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);

  const {
    data: getThemeQueryResponse,
    error: getThemeError,
    loading: isThemeLoading,
  } = useThemeQuery();

  const [
    updateTheme,
    //{ loading: updateThemeLoading, error: updateThemeError },
  ] = useUpdateThemeMutation();

  useEffect(() => {
    getAvailableFonts().then((fonts) => {
      setAvailableFonts(Array.from(fonts));
    });
  }, []);

  const sendMessage = (data: object) => {
    if (!iframeRef.current?.contentWindow) {
      return;
    }
    iframeRef.current.contentWindow.postMessage(data, "*");
  };

  const sendColorMessage = useCallback(
    (colorName: string, colorValue: string) => {
      if (!iframeRef.current?.contentWindow) {
        return;
      }
      sendMessage({
        type: "branding:color",
        data: {
          colorName,
          colorValue,
        },
      });
    },
    []
  );

  const sendFontMessage = useCallback((fontName: string, fontValue: string) => {
    if (!iframeRef.current?.contentWindow) {
      return;
    }
    sendMessage({
      type: "branding:font",
      data: {
        fontName,
        fontValue,
      },
    });
  }, []);

  const sendFontSizeMessage = useCallback(
    (fontName: string, fontSize: number) => {
      if (!iframeRef.current?.contentWindow) {
        return;
      }
      sendMessage({
        type: "branding:fontSize",
        data: {
          fontName,
          fontSize,
        },
      });
    },
    []
  );

  const handleBarColorChange = (color: Color, index: number) => {
    setIsPreviewLoading(true);
    const newBarColors = [...barColors];
    newBarColors[index] = color.toHexString();
    setBarColors(newBarColors);
  };

  const handleTextColorChange = (color: Color) => {
    setIsPreviewLoading(true);
    setTextColor(color.toHexString());
  };

  const handleBackgroundColorChange = (color: Color) => {
    setIsPreviewLoading(true);
    setBackgroundColor(color.toHexString());
  };

  const handleListFontChange = (font: string) => {
    setIsPreviewLoading(true);
    setListFont(font);
  };

  const handleListFontSizeChange = (size: number) => {
    setIsPreviewLoading(true);
    setListFontSize(size);
  };

  const handleTimelineFontChange = (font: string) => {
    setIsPreviewLoading(true);
    setTimelineFont(font);
  };

  const handleTimelineFontSizeChange = (size: number) => {
    setIsPreviewLoading(true);
    setTimelineFontSize(size);
  };

  useEffect(() => {
    if (
      debouncedBarColors.length > 0 &&
      JSON.stringify(debouncedBarColors) !==
        JSON.stringify([
          theme?.accent1,
          theme?.accent2,
          theme?.accent3,
          theme?.accent4,
          theme?.accent5,
          theme?.accent6,
        ])
    ) {
      const overriddenThemeColors = debouncedBarColors.reduce(
        (acc, color, index) => {
          if (
            color !==
            (theme?.[`accent${index + 1}` as keyof typeof theme] as string)
          ) {
            acc[`accent${index + 1}`] = color;
            sendColorMessage(`accent${index + 1}`, color);
          }
          return acc;
        },
        {} as Record<string, string>
      );

      if (Object.keys(overriddenThemeColors).length > 0) {
        updateTheme({
          variables: {
            input: {
              overriddenThemeColors,
            },
          },
        })
          .catch((err) => {
            open("error", t("branding_page.error_updating_theme"));
            console.error("Error updating theme:", err);
          })
          .finally(() => {
            setIsPreviewLoading(false);
          });
      } else {
        setIsPreviewLoading(false);
      }
    } else {
      setIsPreviewLoading(false);
    }
  }, [debouncedBarColors, theme, updateTheme, open, t, sendColorMessage]);

  useEffect(() => {
    if (debouncedTextColor && debouncedTextColor !== theme?.primaryFont) {
      updateTheme({
        variables: {
          input: {
            overriddenThemeColors: {
              primaryFont: debouncedTextColor,
            },
          },
        },
      })
        .then(() => {
          sendColorMessage("primaryFont", debouncedTextColor);
        })
        .catch((err) => {
          open("error", t("branding_page.error_updating_theme"));
          console.error("Error updating theme:", err);
        })
        .finally(() => {
          setIsPreviewLoading(false);
        });
    } else if (debouncedTextColor) {
      setIsPreviewLoading(false);
    }
  }, [
    debouncedTextColor,
    theme?.primaryFont,
    updateTheme,
    open,
    t,
    sendColorMessage,
  ]);

  useEffect(() => {
    if (
      debouncedBackgroundColor &&
      debouncedBackgroundColor !== theme?.primaryBg
    ) {
      updateTheme({
        variables: {
          input: {
            overriddenThemeColors: {
              primaryBg: debouncedBackgroundColor,
            },
          },
        },
      })
        .then(() => {
          sendColorMessage("primaryBg", debouncedBackgroundColor);
        })
        .catch((err) => {
          open("error", t("branding_page.error_updating_theme"));
          console.error("Error updating theme:", err);
        })
        .finally(() => {
          setIsPreviewLoading(false);
        });
    } else if (debouncedBackgroundColor) {
      setIsPreviewLoading(false);
    }
  }, [
    debouncedBackgroundColor,
    theme?.primaryBg,
    updateTheme,
    open,
    t,
    sendColorMessage,
  ]);

  useEffect(() => {
    if (debouncedListFont && debouncedListFont !== theme?.headerFont) {
      updateTheme({
        variables: {
          input: {
            overriddenThemeFonts: {
              header: debouncedListFont,
            },
          },
        },
      })
        .then(() => {
          sendFontMessage("header", debouncedListFont);
        })
        .catch((err) => {
          open("error", t("branding_page.error_updating_theme"));
          console.error("Error updating theme:", err);
        })
        .finally(() => {
          setIsPreviewLoading(false);
        });
    } else if (debouncedListFont) {
      setIsPreviewLoading(false);
    }
  }, [
    debouncedListFont,
    theme?.headerFont,
    updateTheme,
    open,
    t,
    sendFontMessage,
  ]);

  useEffect(() => {
    if (
      debouncedListFontSize !== null &&
      debouncedListFontSize !== theme?.headerFontSize
    ) {
      updateTheme({
        variables: {
          input: {
            fontSizes: {
              header: debouncedListFontSize,
            },
          },
        },
      })
        .then(() => {
          sendFontSizeMessage("header", debouncedListFontSize);
        })
        .catch((err) => {
          open("error", t("branding_page.error_updating_theme"));
          console.error("Error updating theme:", err);
        })
        .finally(() => {
          setIsPreviewLoading(false);
        });
    } else if (debouncedListFontSize !== null) {
      setIsPreviewLoading(false);
    }
  }, [
    debouncedListFontSize,
    theme?.headerFontSize,
    updateTheme,
    open,
    t,
    sendFontSizeMessage,
  ]);

  useEffect(() => {
    if (debouncedTimelineFont && debouncedTimelineFont !== theme?.bodyFont) {
      updateTheme({
        variables: {
          input: {
            overriddenThemeFonts: {
              body: debouncedTimelineFont,
            },
          },
        },
      })
        .then(() => {
          sendFontMessage("body", debouncedTimelineFont);
        })
        .catch((err) => {
          open("error", t("branding_page.error_updating_theme"));
          console.error("Error updating theme:", err);
        })
        .finally(() => {
          setIsPreviewLoading(false);
        });
    } else if (debouncedTimelineFont) {
      setIsPreviewLoading(false);
    }
  }, [
    debouncedTimelineFont,
    theme?.bodyFont,
    updateTheme,
    open,
    t,
    sendFontMessage,
  ]);

  useEffect(() => {
    if (
      debouncedTimelineFontSize !== null &&
      debouncedTimelineFontSize !== theme?.bodyFontSize
    ) {
      updateTheme({
        variables: {
          input: {
            fontSizes: {
              body: debouncedTimelineFontSize,
            },
          },
        },
      })
        .then(() => {
          sendFontSizeMessage("body", debouncedTimelineFontSize);
        })
        .catch((err) => {
          open("error", t("branding_page.error_updating_theme"));
          console.error("Error updating theme:", err);
        })
        .finally(() => {
          setIsPreviewLoading(false);
        });
    } else if (debouncedTimelineFontSize !== null) {
      setIsPreviewLoading(false);
    }
  }, [
    debouncedTimelineFontSize,
    theme?.bodyFontSize,
    updateTheme,
    open,
    t,
    sendFontSizeMessage,
  ]);

  useEffect(() => {
    if (getThemeQueryResponse) {
      const theme = getThemeQueryResponse.theme;
      setTheme(theme);
      setBarColors([
        theme.accent1,
        theme.accent2,
        theme.accent3,
        theme.accent4,
        theme.accent5,
        theme.accent6,
      ]);
      setTextColor(theme.primaryFont);
      setBackgroundColor(theme.primaryBg);
      setListFont(theme.headerFont);
      setListFontSize(theme.headerFontSize);
      setTimelineFont(theme.bodyFont);
      setTimelineFontSize(theme.bodyFontSize);
      if (theme.headerFontSize) {
        sendFontSizeMessage("header", theme.headerFontSize);
      }
      if (theme.bodyFontSize) {
        sendFontSizeMessage("body", theme.bodyFontSize);
      }
    }
  }, [getThemeQueryResponse]);

  if (getThemeError) {
    return <ErrorScreen />;
  }

  if (isThemeLoading) {
    return <GlobalLoader />;
  }

  const iframeSrc = `${
    import.meta.env.ADMIN_ADD_IN_URL
  }content/web.html?a=${encodeURIComponent(
    authToken as string
  )}&chartId=branding-${
    theme?.id
  }&notrack=true&ro=true&from=branding&hasSubscription=1`;

  return (
    <Flex
      style={{
        maxWidth: "1200px",
        margin: "0 auto",
      }}
      justify="center"
      align="stretch"
      vertical
    >
      <Typography style={{ textAlign: "center", marginBottom: token.marginSM }}>
        {!hasSubscription && (
          <>
            <Title level={1}>{t("branding_page.title")}</Title>
            <Paragraph>
              <Trans
                i18nKey="branding_page.free_plan_description"
                components={{
                  a: (
                    <Link
                      to="/subscription"
                      style={{ color: token.colorPrimary }}
                    />
                  ),
                }}
              ></Trans>
            </Paragraph>
            <Button type="primary" onClick={() => navigate("/subscription")}>
              {t("subscribe_now")}
            </Button>
          </>
        )}
        {hasSubscription && (
          <>
            <Title level={1}>{t("branding_page.title")}</Title>
            <Paragraph>{t("branding_page.description")}</Paragraph>
          </>
        )}
      </Typography>
      <Flex gap={token.marginLG} style={{ height: "100%", minHeight: "460px" }}>
        <Flex
          gap={token.marginLG}
          vertical
          align="stretch"
          style={{
            height: "100%",
            maxHeight: "calc(100vh - 130px)",
            overflow: "auto",
            minWidth: 300,
          }}
        >
          <Card
            data-tooltip-id={!hasSubscription ? "jane-tooltip" : undefined}
            data-tooltip-content={t(
              "branding_page.free_plan_subscribe_tooltip"
            )}
            data-tooltip-place="left"
            data-tooltip-float={true}
            title={
              <Flex gap="small">
                <BgColorsOutlined />
                {t("branding_page.colors_title")}
              </Flex>
            }
          >
            <Flex vertical gap="small">
              <Text>{t("branding_page.bar_colors")}</Text>
              <Flex gap="small" wrap="wrap">
                {barColors.map((color, index) => (
                  <ColorPicker
                    disabled={!hasSubscription}
                    key={index}
                    value={color}
                    onChange={(newColor) =>
                      handleBarColorChange(newColor, index)
                    }
                  />
                ))}
              </Flex>
              <Divider size="small" />
              <Flex justify="space-around" style={{ width: "100%" }}>
                <Flex vertical gap="small" align="center">
                  <Text>{t("branding_page.text_color")}</Text>
                  <ColorPicker
                    disabled={!hasSubscription}
                    value={textColor}
                    onChange={handleTextColorChange}
                  />
                </Flex>
                <Flex vertical gap="small" align="center">
                  <Text>{t("branding_page.background_color")}</Text>
                  <ColorPicker
                    disabled={!hasSubscription}
                    value={backgroundColor}
                    onChange={handleBackgroundColorChange}
                  />
                </Flex>
              </Flex>
            </Flex>
          </Card>
          <Card
            data-tooltip-id={!hasSubscription ? "jane-tooltip" : undefined}
            data-tooltip-content={t(
              "branding_page.free_plan_subscribe_tooltip"
            )}
            data-tooltip-place="left"
            data-tooltip-float={true}
            title={
              <Flex gap="small">
                <FontSizeOutlined />
                {t("branding_page.fonts_title")}
              </Flex>
            }
          >
            <Flex vertical gap="middle">
              <Flex vertical gap="small">
                <Text>{t("branding_page.list_of_tasks")}</Text>
                <Flex gap="small">
                  <Select
                    disabled={!hasSubscription}
                    style={{ width: 180 }}
                    value={listFont}
                    onChange={handleListFontChange}
                    options={availableFonts.map((font) => ({
                      label: <span style={{ fontFamily: font }}>{font}</span>,
                      value: font,
                    }))}
                    showSearch
                    labelRender={(props) => (
                      <span style={{ fontFamily: props.value as string }}>
                        {props.label}
                      </span>
                    )}
                  />
                  <Select
                    disabled={!hasSubscription}
                    style={{ width: 70 }}
                    value={listFontSize}
                    onChange={handleListFontSizeChange}
                    options={getAllFontSizes().map((size) => ({
                      label: size,
                      value: size,
                    }))}
                  />
                </Flex>
              </Flex>
              <Flex vertical gap="small">
                <Text>{t("branding_page.timeline_content")}</Text>
                <Flex gap="small">
                  <Select
                    disabled={!hasSubscription}
                    style={{ width: 180 }}
                    value={timelineFont}
                    onChange={handleTimelineFontChange}
                    options={availableFonts.map((font) => ({
                      label: <span style={{ fontFamily: font }}>{font}</span>,
                      value: font,
                    }))}
                    showSearch
                    labelRender={(props) => (
                      <span style={{ fontFamily: props.value as string }}>
                        {props.label}
                      </span>
                    )}
                  />
                  <Select
                    disabled={!hasSubscription}
                    style={{ width: 70 }}
                    value={timelineFontSize}
                    onChange={handleTimelineFontSizeChange}
                    options={getAllFontSizes().map((size) => ({
                      label: size,
                      value: size,
                    }))}
                  />
                </Flex>
              </Flex>
            </Flex>
          </Card>
        </Flex>
        <Card
          style={{ position: "relative", width: "100%", height: "460px" }}
          styles={{
            body: {
              backgroundColor: token.colorBgTextActive,
            },
          }}
          title={
            <Flex gap="small">
              <EyeOutlined />
              {t("branding_page.preview")}
            </Flex>
          }
        >
          {theme && (
            <Flex
              vertical
              gap={token.marginLG}
              style={{ position: "relative", width: "100%", height: "350px" }}
            >
              {isIframeLoading && <GlobalLoader position="absolute" />}
              <iframe
                ref={iframeRef}
                src={iframeSrc}
                width="100%"
                height="100%"
                title="Chart"
                onLoad={() => setIsIframeLoading(false)}
                style={{
                  display: isIframeLoading ? "none" : "block",
                  border: "none",
                  boxShadow: `0 0 0 1px ${token.colorBorderSecondary}`,
                  borderRadius: token.borderRadiusLG,
                  height: "calc(100vh - 130px)",
                }}
              />
              {isPreviewLoading && (
                <GlobalLoader position="absolute" transparent={true} />
              )}
            </Flex>
          )}
        </Card>
      </Flex>
    </Flex>
  );
};

export default BrandingPage;
